
import React, { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Button } from "@/components/ui/button";
import { Loader2, Save, XCircle, AlertCircle } from 'lucide-react';
import { Form } from "@/components/ui/form";
import { EventFormValues } from '../schemas/eventFormSchema';
import { DialogFooter } from "@/components/ui/dialog";
import BasicEventInfo from './BasicEventInfo';
import DateTimeFields from './DateTimeFields';
import VenueInfo from './VenueInfo';
import AttendanceOptions from './AttendanceOptions';
import ProfessionSelector from './ProfessionSelector';
import EventPreview from './EventPreview';
import { Alert, AlertDescription } from "@/components/ui/alert";
import EventStatus from './EventStatus';

interface EventEditorTabContentProps {
  form: UseFormReturn<EventFormValues>;
  isSubmitting: boolean;
  onSubmit: (values: EventFormValues) => void;
  onClose: () => void;
}

const EventEditorTabContent = ({
  form,
  isSubmitting,
  onSubmit,
  onClose
}: EventEditorTabContentProps) => {
  const { formState } = form;
  const { errors, isDirty, isValid } = formState;

  // Log form validation errors for debugging
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      console.log("Form validation errors:", errors);
    }
  }, [errors]);

  const handleFormSubmit = (values: EventFormValues) => {
    console.log("EventEditorTabContent - Form submitted with values:", values);
    onSubmit(values);
  };

  // This will run when submission is attempted but form is invalid
  const handleInvalidSubmit = () => {
    console.error("Form submission failed due to validation errors:", errors);
  };

  // Count total errors
  const errorCount = Object.keys(errors).length;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit, handleInvalidSubmit)} className="flex flex-col h-full max-h-[calc(90vh-120px)]">
        {errorCount > 0 && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Az űrlap {errorCount} hibát tartalmaz. Kérjük javítsa a piros mezőket a mentés előtt.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 overflow-y-auto flex-1 pr-1">
          {/* Left column - form fields */}
          <div className="space-y-6 md:col-span-2">
            <div className="space-y-6">
              <BasicEventInfo form={form} />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <DateTimeFields form={form} />
                <VenueInfo form={form} />
              </div>

              {/* Szakma választó komponens */}
              <ProfessionSelector form={form} />

              <AttendanceOptions form={form} />
              
              {/* Moved status selector to the end of the form */}
              <EventStatus form={form} />
            </div>
          </div>

          {/* Right column - preview */}
          <div className="md:col-span-1 relative">
            <div className="sticky top-2">
              <EventPreview control={form.control} />
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 p-4 mt-4 border-t bg-white">
          <Button
            variant="outline"
            type="button"
            onClick={onClose}
            className="gap-2"
          >
            <XCircle className="h-4 w-4" />
            Mégse
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="gap-2 bg-medihub-primary hover:bg-medihub-primary/90"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Mentés...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Mentés {!isValid && errorCount > 0 ? `(${errorCount} hiba)` : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};

export default EventEditorTabContent;
